import api from '@/api';
import { getInstance } from '@/auth';

const claimNamespace = 'https://mychapman.chapmanauto.com';

export const state = {
    customer: {},
    employee: {},
    role: 'customer', // customer, employee
};

export const getters = {
    customerId(state) {
        // "sub" used on details from $auth
        // "user_id" used on details from api findCustomer
        return state.customer.sub || state.customer.user_id;
    },
    customerName(state) {
        const firstName = state.customer.firstName || '';
        const lastName = state.customer.lastName || '';
        return `${firstName} ${lastName}`.trim();
    },
    customerVerified(state) {
        return state.customer.email_verified;
    },
    employeeArkona(state) {
        const arkonaKey = `${claimNamespace}/arkona`;
        return state.employee[arkonaKey];
    },
    employeeId(state) {
        return state.employee.sub;
    },
    employeeName(state) {
        const firstName = state.employee.given_name || '';
        const lastName = state.employee.family_name || '';
        return `${firstName} ${lastName}`.trim();
    },
    employeeVerified(state) {
        return state.employee.email_verified;
    },
    isEmployee(state) {
        return state.role === 'employee';
    },
    userEmail(state) {
        return state[state.role].email;
    },
    userId(state) {
        return state[state.role].sub;
    },
    userName(state, getters) {
        return state.role === 'employee'
            ? getters.employeeName
            : getters.customerName;
    },
};

export const mutations = {
    SET_CUSTOMER_DETAILS(state, details) {
        state.customer = details;
    },
    SET_EMPLOYEE_DETAILS(state, details) {
        state.employee = details;
    },
    SET_USER_ROLE(state, role) {
        state.role = role;
    },
    UPDATE_CUSTOMER_DETAILS(state, details) {
        state.customer = {
            ...state.customer,
            ...details,
        };
    },
    UPDATE_EMPLOYEE_DETAILS(state, details) {
        state.employee = {
            ...state.employee,
            ...details,
        };
    },
};

export const actions = {
    async CREATE_CUSTOMER_ACCOUNT({ commit }, payload) {
        const authService = getInstance();
        const token = await authService.getTokenSilently();

        try {
            const { data, status } = await api.user.createAccount({
                payload,
                token,
            });

            if (status === 200 && data) {
                // Create details (include empty address object)
                const customerDetails = {
                    ...payload,
                    address: {},
                    email_verified: false,
                    user_id: data,
                };

                // Delete password that is no longer needed
                delete customerDetails.password;

                commit('SET_CUSTOMER_DETAILS', customerDetails);

                return { status };
            } else {
                throw new Error('Error creating customer account');
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);

            // Get error returned from Auth0
            if (error.response && error.response.data) {
                const { message, statusCode } = error.response.data;
                return {
                    message,
                    status: statusCode,
                };
            }

            return {
                message: error.message,
                status: 400,
            };
        }
    },
    async FETCH_CUSTOMER_PERSONAL_DETAILS({ commit, state }, customerId) {
        const authService = getInstance();
        const token = await authService.getTokenSilently();

        const { data, status } = await api.user.fetchPersonalDetails({
            customerId,
            token,
        });

        if (status === 200 && data) {
            // Delete since Auth0 is source of truth for email
            delete data.email;

            commit('UPDATE_CUSTOMER_DETAILS', data);

            // Return updated details
            return state.customer;
        } else {
            throw new Error('Error fetching customer details');
        }
    },
    SET_USER_DETAILS({ commit }, details) {
        // Get roles from token details
        const rolesKey = `${claimNamespace}/roles`;
        const roles = details[rolesKey];
        const isEmployee = roles.includes('Showroom');

        if (isEmployee) {
            commit('SET_EMPLOYEE_DETAILS', details);
            commit('SET_USER_ROLE', 'employee');
        } else {
            commit('SET_CUSTOMER_DETAILS', details);
            commit('SET_USER_ROLE', 'customer');
        }
    },
    UPDATE_USER_DETAILS({ commit, state }, details) {
        if (state.role === 'employee') {
            commit('UPDATE_EMPLOYEE_DETAILS', details);
        } else {
            commit('UPDATE_CUSTOMER_DETAILS', details);
        }
    },
};
